import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowDown, BookOpen, Plus, Minus } from 'lucide-react';
import StackLayersArt from './landing/StackLayersArt';
import { SitePage, Btn, Go, PrimaryCta, InstallButton, TO, asset } from './landing/SiteChrome';
import { MODEL_GROUPS, MODES } from './landing/content';

const CAPABILITIES = [
  { k: 'Model choice', v: 'Private, approved external, or both — set per workload.' },
  { k: 'Connected context', v: 'Repository knowledge shared across coding tasks.' },
  { k: 'Team coordination', v: 'Decisions and agent sessions on one topic.' },
  { k: 'One control substrate', v: 'Every component shares one model-access and context policy.' },
];

const DG_STAGES = [
  { num: '01', name: 'Your teams', owner: 'LatentStack components', ownerTo: TO.stack, arrow: 'right', tint: '#E4E7DC', items: [{ t: 'LatentCode', to: TO.docsLatentcode }, { t: 'Workspace', to: TO.stack }, { t: 'SkillPatch', to: TO.stack }] },
  { num: '02', name: 'Access', owner: 'In LatentGateway', ownerTo: TO.gateway, arrow: 'right', tint: '#FBF1CC', items: [{ t: 'Per-user keys' }, { t: 'Tiers and projects' }, { t: 'Rate limits per model and user' }] },
  { num: '03', name: 'Guardrails', owner: 'In LatentGateway', ownerTo: TO.gateway, arrow: 'right', tint: '#FBE7DC', items: [{ t: 'L1 pattern and checksum screening' }, { t: 'Block, mask or allow' }, { t: 'Rules set by your admins' }] },
  { num: '04', name: 'Routing', owner: 'In LatentGateway', ownerTo: TO.gateway, arrow: 'down', tint: '#F4B9A0', items: [{ t: 'Resolve the model' }, { t: 'Meter usage and cost' }, { t: 'Enforce budgets' }] },
];

const DG_FACTS = {
  def: [
    { k: 'Inside', v: 'LatentCode, Workspace and SkillPatch; access control, guardrails and routing in LatentGateway; private models on your GPUs.' },
    { k: 'Outside', v: 'Approved third-party model providers, only where you have approved them.' },
    { k: 'What crosses', v: 'Permitted work, sent by routing to an approved provider. It is the only crossing shown.' },
    { k: 'What cannot', v: 'Work you restrict to private models. It has no external route and no fallback to a hosted provider.' },
  ],
  air: [
    { k: 'Inside', v: 'LatentCode, Workspace and SkillPatch; access control, guardrails and routing in LatentGateway; private models on your GPUs.' },
    { k: 'Outside', v: 'Nothing. In the air-gapped state there is no external zone.' },
    { k: 'What crosses', v: 'Nothing. Routing resolves to private models only.' },
    { k: 'What cannot', v: 'All work. No route leaves the environment in this state.' },
  ],
};

// The "Everything the work needs" layer grid. Each side column is a stack of cells
// whose heights animate between collapsed (hs) and expanded (he) as the stack art opens.
const LAYER_SIDES = [
  [
    { he: '215.5px', hs: '74.0px', layer: { num: '04', numColor: '#4A7A9B', name: 'COORDINATION', comps: 'Workspace', desc: 'The human layer: discussions, decisions, a feature wiki and agent sessions around one topic.', delay: '300ms' } },
    { he: '215.5px', hs: '125.5px' },
    { he: '215.5px', hs: '74.0px', layer: { num: '01', numColor: '#BF9718', name: 'CONTEXT', comps: 'LatentGraph · SkillPatch · Trace analytics', desc: 'Repository knowledge, team practice and the guidance that improves both. Agent-neutral: the context goes to whichever agent you run.', delay: '380ms' } },
    { he: '215.5px', hs: '125.5px' },
  ],
  [
    { he: '107.8px', hs: '62.8px' },
    { he: '215.5px', hs: '74.0px', layer: { num: '03', numColor: '#2F6B4A', name: 'EXECUTION', comps: 'LatentCode · Managed agents', desc: 'Goal-based agent execution locally or in configured sandboxes, with execution kept separate from model hosting.', delay: '340ms' } },
    { he: '215.5px', hs: '125.5px' },
    { he: '215.5px', hs: '74.0px', layer: { num: '02', numColor: '#C4501F', name: 'MODEL ACCESS', comps: 'LatentGateway', desc: 'One policy substrate shared by every component: endpoints, model tiers, team permissions, and the restricted / permitted split.', delay: '420ms' } },
    { he: '107.8px', hs: '62.8px' },
  ],
];

// Dots on the stage edges that travel up to each layer's row as the stack opens.
const LAYER_MARKERS = [
  { side: 'left', be: '714px', bs: '249px', d: '300ms', color: '#7FA6C4' },
  { side: 'left', be: '348px', bs: '193px', d: '380ms', color: '#E0B72C' },
  { side: 'right', be: '532px', bs: '222px', d: '340ms', color: '#57CC88' },
  { side: 'right', be: '166px', bs: '166px', d: '420ms', color: '#E1642F' },
];

const COMPLIANCE_QS = [
  { num: '01', topic: 'Data residency', tint: '#E4E7DC', question: 'Where does our data go?', answer: 'Models can run on your own GPUs. Work you restrict to private models stays inside your network, with no external fallback.', limit: 'Work you permit to use an approved external provider leaves your network, under that provider’s terms.', linkText: 'Read the boundary', to: TO.deployment },
  { num: '02', topic: 'Access control', tint: '#FBF1CC', question: 'Who can use which model?', answer: 'Per-user keys, tiers and projects. Rate limits per model and per user, in both directions, enforced at the gateway.', limit: 'Controls apply to traffic routed through LatentGateway.', linkText: 'See the controls', to: TO.gateway },
  { num: '03', topic: 'Audit trail', tint: '#FBE7DC', question: 'Can we trace a request?', answer: 'Each request through the gateway is priced, logged and attributed by user, project, model and provider.', limit: 'Requests that do not pass through the gateway are not recorded here.', linkText: 'See what is logged', to: TO.security },
  { num: '04', topic: 'Content safety', tint: '#F4B9A0', question: 'What is screened before routing?', answer: 'L1 pattern and checksum rules screen each input. Admins choose to block, mask or allow.', limit: 'Some personal data, such as names in free text, can pass. Shipped levels are on the LatentGateway page.', linkText: 'Screening levels and limits', to: TO.gateway },
];

const METRICS = [
  { label: 'Claude Code tasks resolved', delta: '+2 tasks', deltaBg: '#FBE7DC', deltaInk: '#B84A1C', before: '8 / 14', after: '10 / 14', pctB: '57%', pctA: '71%', fill: '#E1642F' },
  { label: 'API cost per resolved task', delta: '66% lower', deltaBg: '#FBF1CC', deltaInk: '#6B5410', before: '$3.93', after: '$1.33', pctB: '100%', pctA: '34%', fill: '#F2CE49' },
];

const STEPS = [
  { num: '01', name: 'Agree the requirement', desc: 'Clarify the fields, access rules and acceptance criteria. Keep the decision with the topic.', role: 'People decide' },
  { num: '02', name: 'Prepare the work', desc: 'Bring relevant repository context and team skills to an agent using the permitted model environment.', role: 'Your boundaries' },
  { num: '03', name: 'Delegate implementation', desc: 'A main agent coordinates persistent side agents. They implement the change and run relevant checks.', role: 'Agents execute' },
  { num: '04', name: 'Review the result', desc: 'People assess the change and make critical decisions. The result stays connected to the work.', role: 'People review' },
];

const FAQS = [
  { q: 'Do we have to take all of it?', a: 'No. An enterprise can subscribe to the whole stack or to individual components.' },
  { q: 'Does it replace our current coding agent?', a: 'Not necessarily. Context can be supplied to supported third-party agents; LatentCode is available when a team wants it.' },
  { q: 'Does our code leave our network?', a: 'It depends on the configuration you choose. Work restricted to private models has no external fallback. Work you permit to use approved third-party models sends the permitted inputs to that provider under that provider’s terms.' },
  { q: 'Do you train on our code?', a: 'No training on Customer Content.' },
  { q: 'What does it cost?', a: 'Platform pricing is per seat; underlying model usage is paid by the customer to the provider. Component-level packaging and consumption terms for managed execution are set during scoping.' },
  { q: 'Can we evaluate on our own code?', a: 'Yes — a scoped POC around one team, one repository and one workflow with agreed acceptance criteria.' },
];

/* ── Hero ────────────────────────────────────────────────────────────────── */
// WebKit (Safari, and every browser on iOS) plays VP9 WebM but drops its alpha
// channel, painting the transparent areas black; it only renders transparency
// from HEVC-with-alpha. Chromium on macOS can decode HEVC too, so <source> type
// sniffing can't separate them; pick by engine instead.
const isWebKit = typeof navigator !== 'undefined'
  && /AppleWebKit/.test(navigator.userAgent)
  && !/Chrome|Chromium|Edg|Android/.test(navigator.userAgent);
const HERO_LOOP = isWebKit ? '/landing/hero-loop.mov' : '/landing/hero-loop.webm';

function Hero() {
  // Some browsers ignore the muted attribute React renders, which blocks autoplay;
  // set it on the element and start playback explicitly.
  const videoRef = useRef(null);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = true;
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  }, []);

  return (
    <section>
      <div className="lsl-wrap lsl-hero-main">
        <div className="lsl-hero-copy">
          <div className="lsl-hero-chip">every team · your infrastructure · your data stays in</div>
          <h1 className="lsl-h1">Give every team the <span>superpower of AI.</span></h1>
          <p className="lsl-hero-lede">
            One secure deployment puts AI to work across your enterprise, on <strong>infrastructure you own</strong>,
            with your data <strong>never leaving your walls</strong>. Access, limits, audit and guardrails come built in.
          </p>
          <div className="lsl-hero-ctas">
            <PrimaryCta size="lg" />
            <InstallButton size="lg" />
            <Btn to={TO.docs} variant="ghost" size="lg" iconLeft={BookOpen}>Read the docs</Btn>
          </div>
        </div>
        <div className="lsl-hero-art">
          <video
            ref={videoRef}
            src={asset(HERO_LOOP)}
            autoPlay
            loop
            muted
            playsInline
            aria-label="Isometric illustration: three packets travel a closed loop through five coloured pedestals around a central ember core, and a lift returns each packet to the top."
          />
        </div>
      </div>
      <div className="lsl-wrap lsl-runs">
        <div className="lsl-runs-row">
          <span className="lsl-label">Runs in</span>
          <div className="lsl-runs-list">
            <Go to={TO.deployment}>Your cloud</Go><span aria-hidden>·</span>
            <Go to={TO.deployment}>A GPU cloud provider</Go><span aria-hidden>·</span>
            <Go to={TO.deployment}>On-premises</Go><span aria-hidden>·</span>
            <Go to={TO.deployment}>Fully air-gapped, if required</Go>
          </div>
        </div>
        <p className="lsl-runs-note">
          A GPU cloud provider is a specialist host that rents dedicated GPU capacity under your own account. Work you
          restrict to private models stays inside your network, with no external fallback. Work you permit may use
          approved external providers.
        </p>
      </div>
    </section>
  );
}

function CapabilityStrip() {
  return (
    <section className="lsl-strip">
      <div className="lsl-wrap lsl-strip-grid">
        {CAPABILITIES.map((c) => (
          <div key={c.k}><div className="lsl-label">{c.k}</div><p>{c.v}</p></div>
        ))}
      </div>
    </section>
  );
}

/* ── System model: what runs inside the boundary, and what can leave ─────── */
function SystemModel() {
  const [airGapped, setAirGapped] = useState(false);
  const facts = airGapped ? DG_FACTS.air : DG_FACTS.def;

  return (
    <section id="system-model" aria-labelledby="sys-h" className="lsl-wrap lsl-pad">
      <div className="lsl-head-row">
        <div style={{ flex: '1 1 460px', minWidth: 0 }}>
          <div className="lsl-label">System model</div>
          <h2 id="sys-h" className="lsl-h2 lsl-h2--after-label" style={{ maxWidth: '22ch' }}>
            What runs inside your environment, and what can leave it.
          </h2>
        </div>
        <div role="group" aria-label="Diagram state" className="lsl-toggle">
          <button type="button" aria-pressed={!airGapped} onClick={() => setAirGapped(false)}>With approved providers</button>
          <button type="button" aria-pressed={airGapped} onClick={() => setAirGapped(true)}>Air-gapped</button>
        </div>
      </div>

      <div role="group" aria-label="Your environment" className="lsl-env">
        <div className="lsl-env-head">
          <span className="lsl-eyebrow">Your environment · inside your network boundary</span>
          {airGapped && <span className="lsl-env-badge">Air-gapped state · no external zone</span>}
        </div>
        <div className="lsl-stages">
          {DG_STAGES.map((s) => (
            <div key={s.num} className="lsl-stage" style={{ background: s.tint }}>
              <div className="lsl-stage-top">
                <span className="lsl-eyebrow">{s.num}</span>
                {s.arrow === 'down' ? <ArrowDown size={16} aria-hidden /> : <ArrowRight size={16} aria-hidden />}
              </div>
              <h3 className="lsl-card-title">{s.name}</h3>
              <Go to={s.ownerTo} className="lsl-stage-owner">{s.owner}</Go>
              <ul>
                {s.items.map((it) => (
                  <li key={it.t}>{it.to ? <Go to={it.to}>{it.t}</Go> : <span>{it.t}</span>}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="lsl-zones">
          <div className="lsl-zone lsl-zone--private">
            <span className="lsl-eyebrow">Private models · your GPUs, self-hosted</span>
            <div className="lsl-pills">
              <span className="lsl-pill">llama-3.1-70b</span>
              <span className="lsl-pill">qwen2.5-72b</span>
            </div>
            <p>Restricted work ends here. It has no route out of your environment and no fallback to a hosted provider.</p>
          </div>
          {airGapped ? (
            <div className="lsl-zone lsl-zone--none">
              <span className="lsl-eyebrow">No exit</span>
              <h3 className="lsl-card-title">Private models only</h3>
              <p>In the air-gapped state, routing resolves to private models only. There is no external zone and no crossing.</p>
            </div>
          ) : (
            <div className="lsl-zone lsl-zone--exit">
              <div className="lsl-zone-top">
                <span className="lsl-eyebrow">Boundary exit</span>
                <ArrowDown size={16} aria-hidden />
              </div>
              <h3 className="lsl-card-title">Permitted work only</h3>
              <p>Routing sends work you have permitted to an approved third-party provider. Restricted work cannot take this path.</p>
            </div>
          )}
        </div>
      </div>

      <div className="lsl-below">
        <dl className="lsl-facts">
          {facts.map((f) => (
            <div key={f.k}><dt className="lsl-label">{f.k}</dt><dd>{f.v}</dd></div>
          ))}
        </dl>
        {!airGapped && (
          <div className="lsl-crossing">
            <div aria-hidden className="lsl-crossing-line" style={{ height: 24 }} />
            <span className="lsl-crossing-tag">Crosses the boundary · permitted work only</span>
            <div aria-hidden className="lsl-crossing-line" style={{ height: 16 }} />
            <ArrowDown size={16} aria-hidden />
            <div role="group" aria-label="External" className="lsl-external">
              <span className="lsl-eyebrow">External · outside your boundary</span>
              <h3 className="lsl-card-title">Approved third-party providers</h3>
              <div className="lsl-pills">
                <span className="lsl-pill">anthropic</span>
                <span className="lsl-pill">openai</span>
                <span className="lsl-pill">google</span>
              </div>
              <p>Each receives only the inputs you permit, under that provider's terms.</p>
            </div>
          </div>
        )}
      </div>
      <div style={{ marginTop: 'var(--space-8)' }}>
        <Go to={TO.deployment} className="lsl-more">Read the full boundary, per deployment mode</Go>
      </div>
    </section>
  );
}

function ModelChoice() {
  return (
    <section id="models" aria-labelledby="models-h" className="lsl-band lsl-band--both">
      <div className="lsl-wrap lsl-pad">
        <div className="lsl-label">Model choice</div>
        <h2 id="models-h" className="lsl-h2 lsl-h2--after-label" style={{ maxWidth: '24ch' }}>
          Name the model. See which side of the boundary it runs on.
        </h2>
        <div className="lsl-models">
          {MODEL_GROUPS.map((g) => (
            <div key={g.title} className="lsl-tcard">
              <div className="lsl-tcard-head" style={{ background: g.tint }}>
                <span className="lsl-eyebrow">{g.label}</span>
                <h3>{g.title}</h3>
              </div>
              <div className="lsl-pills">
                {g.ids.map((id) => <span key={id} className="lsl-pill">{id}</span>)}
              </div>
            </div>
          ))}
        </div>
        <div className="lsl-footnote-row">
          <p style={{ color: 'var(--text-primary)' }}>
            Selecting a hosted provider sends the permitted inputs to that provider, under that provider's terms.
            Restricted work cannot be routed to a hosted provider.
          </p>
          <Go to={TO.deployment} className="lsl-more">See what crosses the boundary</Go>
        </div>
        <p className="lsl-models-note">
          Identifiers shown in the provider-prefixed format the gateway accepts. The full compatibility list, with
          verification dates, is on the <Link to={TO.gateway}>LatentGateway page</Link>.
        </p>
      </div>
    </section>
  );
}

/* ── The stack: four layers that slide apart on hover/focus ──────────────── */
function StackLayers() {
  return (
    <div id="stack" className="lsl-layers-outer">
      <section className="lg-sec" tabIndex={0} aria-label="The LatentStack layers. Expands to show each layer in detail.">
        <div className="lg-head">
          <div style={{ maxWidth: 720 }}>
            <h2>Everything the work needs.<br />Connected in one stack.</h2>
            <p>Four layers, seven components. Take the whole stack, or only the layers you need.</p>
          </div>
          <div className="lg-cta">
            <span className="lg-hint">HOVER TO OPEN LATENTSTACK</span>
            <Link to={TO.docs}>Explore LatentStack</Link>
          </div>
        </div>
        <div className="lg-row">
          <LayerSide cells={LAYER_SIDES[0]} />
          <div className="lg-stage">
            {LAYER_MARKERS.map((m) => {
              const vars = { '--be': m.be, '--bs': m.bs, '--d': m.d };
              return (
                <React.Fragment key={m.color}>
                  <span className="lg-mk" style={{ ...vars, [m.side]: -4, background: m.color }} />
                  <span className="lg-leader" style={{ ...vars, [m.side]: 4 }} />
                </React.Fragment>
              );
            })}
            <StackLayersArt />
          </div>
          <LayerSide cells={LAYER_SIDES[1]} />
        </div>
      </section>
    </div>
  );
}

function LayerSide({ cells }) {
  return (
    <div className="lg-side">
      {cells.map((c, i) => {
        const line = i < cells.length - 1 ? ' lg-line' : '';
        if (!c.layer) return <div key={i} className={`lg-cell${line}`} style={{ '--he': c.he, '--hs': c.hs }} />;
        const l = c.layer;
        return (
          <div key={i} className={`lg-cell lg-pad${line}`} style={{ '--he': c.he, '--hs': c.hs }}>
            <div className="lg-eyebrow"><span style={{ color: l.numColor }}>{l.num}</span> {l.name}</div>
            <div className="lg-reveal" style={{ '--d': l.delay }}>
              <div className="lg-comps">{l.comps}</div>
              <p className="lg-desc">{l.desc}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Compliance: the four questions a reviewer brings ────────────────────── */
function ComplianceQuestions() {
  const [open, setOpen] = useState({});
  const toggle = (i) => setOpen((o) => ({ ...o, [i]: !o[i] }));

  return (
    <section id="compliance" aria-labelledby="cq-h" className="lsl-wrap lsl-pad">
      <div className="lsl-label">Compliance review</div>
      <h2 id="cq-h" className="lsl-h2 lsl-h2--after-label" style={{ maxWidth: '22ch' }}>The four questions a reviewer brings.</h2>
      <p className="lsl-cq-lede">
        Running on your own infrastructure addresses where data sits. Access, audit and content safety need controls a
        reviewer can inspect. Each answer links to the page that states it in full.
      </p>
      <div className="lsl-cq-grid">
        {COMPLIANCE_QS.map((q, i) => (
          <div key={q.num} className="lsl-tcard lsl-cq">
            <div className="lsl-tcard-head" style={{ background: q.tint }}>
              <span className="lsl-eyebrow">{q.num} · {q.topic}</span>
              <h3>{q.question}</h3>
            </div>
            <p className="lsl-cq-answer">{q.answer}</p>
            <div className="lsl-cq-foot">
              <button type="button" className="lsl-cq-toggle" aria-expanded={!!open[i]} onClick={() => toggle(i)}>
                <span>{open[i] ? 'Hide the limit' : 'Read the limit'}</span>
                {open[i] ? <Minus size={16} aria-hidden /> : <Plus size={16} aria-hidden />}
              </button>
              {open[i] && <p className="lsl-cq-limit">{q.limit}</p>}
              <Go to={q.to} className="lsl-cq-link">{q.linkText}</Go>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Deployment modes: flip cards ────────────────────────────────────────── */
function DeploymentModes() {
  return (
    <section id="deployment" className="lsl-band lsl-band--both">
      <div className="lsl-wrap lsl-pad">
        <h2 className="lsl-h2" style={{ maxWidth: '26ch' }}>Choose where it runs. Keep your options open.</h2>
        <div className="lsl-modes">
          {MODES.map((m) => (
            <div key={m.num} className="lsl-flip" tabIndex={0}>
              <div className="lsl-flip-inner">
                <div className="lsl-face">
                  <div className="lsl-face-top">
                    <span className="lsl-eyebrow">{m.eyebrow}</span>
                    <span className="lsl-eyebrow">{m.num}</span>
                  </div>
                  <div className="lsl-mode-art" style={{ background: m.bg }}>
                    <img src={asset(m.art)} alt="" aria-hidden />
                  </div>
                  <h3>{m.name}<sup>*</sup></h3>
                  <p className="lsl-mode-body">{m.body}</p>
                  <span className="lsl-eyebrow lsl-mode-hint"><span><span className="lsl-hint-hover">Hover</span><span className="lsl-hint-touch">Tap</span> for details</span><span aria-hidden>↗</span></span>
                </div>
                <div className="lsl-face lsl-face--back">
                  <div className="lsl-face-top">
                    <span className="lsl-eyebrow">{m.eyebrow}</span>
                    <span className="lsl-eyebrow">{m.num}</span>
                  </div>
                  <h3 style={{ background: m.bg }}>{m.name}</h3>
                  <div className="lsl-mode-rows">
                    <div className="lsl-mode-row"><span className="lsl-eyebrow">Operated by</span><strong>{m.opBy}</strong></div>
                    <div className="lsl-mode-row"><span className="lsl-eyebrow">Models</span><strong>{m.models}</strong></div>
                    <div className="lsl-mode-row lsl-mode-row--center">
                      <span className="lsl-eyebrow">External calls</span>
                      <span className="lsl-mode-egress" style={{ background: m.bg }}>{m.egress}</span>
                    </div>
                  </div>
                  <p className="lsl-mode-note">{m.note}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lsl-footnote-row">
          <p style={{ color: 'var(--text-secondary)' }}>
            Restricted work stays on private models with no external fallback. Work you permit to use approved
            third-party models sends the permitted inputs to that provider, under that provider's terms.
          </p>
          <Go to={TO.deployment} className="lsl-more">See what leaves your environment</Go>
        </div>
      </div>
    </section>
  );
}

/* ── Evidence ────────────────────────────────────────────────────────────── */
function Evidence() {
  return (
    <section id="evidence" className="lsl-wrap lsl-pad">
      <div className="lsl-head-row">
        <h2 className="lsl-h2" style={{ maxWidth: '24ch' }}>Better context. A measurable difference.</h2>
        <Btn to={TO.contact} variant="outline" iconRight={ArrowRight}>Ask for the full evidence base</Btn>
      </div>
      <div className="lsl-evidence">
        <div className="lsl-metrics">
          {METRICS.map((m) => (
            <div key={m.label} className="lsl-metric">
              <div className="lsl-metric-top">
                <span className="lsl-eyebrow">{m.label}</span>
                <span className="lsl-delta" style={{ color: m.deltaInk, background: m.deltaBg }}>{m.delta}</span>
              </div>
              <div className="lsl-metric-big">
                <strong>{m.after}</strong>
                <span>from {m.before}</span>
              </div>
              <div className="lsl-bars">
                <div className="lsl-bar">
                  <span className="lsl-eyebrow">Without LatentGraph</span>
                  <span className="lsl-bar-value">{m.before}</span>
                  <div className="lsl-bar-track"><div style={{ width: m.pctB, background: '#A9AD9F' }} /></div>
                </div>
                <div className="lsl-bar lsl-bar--after">
                  <span className="lsl-eyebrow">With LatentGraph</span>
                  <span className="lsl-bar-value">{m.after}</span>
                  <div className="lsl-bar-track"><div style={{ width: m.pctA, background: m.fill }} /></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lsl-evidence-note">
          14 selected hard tasks across two repositories. API cost includes failed attempts. Company-reported
          repository-context evaluation, June 3, 2026; not a measure of whole-factory productivity.
        </div>
      </div>
    </section>
  );
}

/* ── Workflow ────────────────────────────────────────────────────────────── */
function Workflow() {
  return (
    <section id="workflow" className="lsl-band">
      <div className="lsl-wrap lsl-pad">
        <div className="lsl-wf-head">
          <h2 className="lsl-h2" style={{ maxWidth: '20ch', textWrap: 'pretty' }}>From a requirement to a reviewed change.</h2>
          <p>People set the direction. Agents help move the work forward.</p>
        </div>
        <div className="lsl-wf-art">
          <img src={asset('/landing/workflow.gif')} alt="A robotic hand and two human hands place pieces into the same puzzle grid" />
        </div>
        <div className="lsl-steps">
          {STEPS.map((s) => (
            <div key={s.num} className="lsl-box lsl-step">
              <div className="lsl-step-top"><span>{s.num}</span><span>{s.role}</span></div>
              <h3>{s.name}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="lsl-wf-foot">
          <Btn to={TO.docs} variant="outline" iconRight={ArrowRight}>Walk through the workflow</Btn>
          <span>Reporting / CSV export · illustrative workflow</span>
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="lsl-band lsl-band--both">
      <div className="lsl-wrap lsl-pad">
        <h2 className="lsl-h2 lsl-faq-title">The practical details.</h2>
        <div className="lsl-faqs">
          {FAQS.map((f) => (
            <div key={f.q} className="lsl-box">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VersionNote() {
  return (
    <section className="lsl-wrap lsl-version">
      <div className="lsl-version-row">
        <div>
          <span className="lsl-label">Platform v0.9</span>
          <p>LatentStack is at version 0.9. Each component page states its current maturity.</p>
        </div>
        <Link to={TO.docs} className="lsl-more">Component status</Link>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="lsl-cta" style={{ backgroundImage: `url("${asset('/landing/cta-ground.svg')}")` }}>
      <div className="lsl-wrap lsl-cta-inner">
        <h2>What would you <br />build next?</h2>
        <p>
          Bring a repository, a workflow and your model requirements. <br />We will scope an evaluation around one team,
          one repository and one workflow, with agreed acceptance criteria.
        </p>
        <div className="lsl-cta-actions">
          <Btn to={TO.evaluation} size="lg" iconRight={ArrowRight}>Request an evaluation</Btn>
        </div>
      </div>
    </section>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
// Gentle scroll parallax: each section below the hero drifts up to ±28px toward
// the viewport centre. Skipped for reduced-motion.
function useSectionParallax(rootRef) {
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0;
    const tick = () => {
      raf = 0;
      const vh = window.innerHeight;
      rootRef.current?.querySelectorAll('main section').forEach((sec, i) => {
        if (i === 0) return;
        const r = sec.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const y = Math.max(-28, Math.min(28, (((r.top + r.height / 2) - vh / 2) / vh) * 36));
        for (const ch of sec.children) {
          ch.style.transform = `translate3d(0,${y.toFixed(1)}px,0)`;
          ch.style.willChange = 'transform';
        }
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(tick); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [rootRef]);
}

function Landing() {
  const rootRef = useRef(null);
  useSectionParallax(rootRef);

  return (
    <SitePage rootRef={rootRef}>
      <Hero />
      <CapabilityStrip />
      <SystemModel />
      <ModelChoice />
      <StackLayers />
      <ComplianceQuestions />
      <DeploymentModes />
      <Evidence />
      <Workflow />
      <Faq />
      <VersionNote />
      <ClosingCta />
    </SitePage>
  );
}

export default Landing;
